import {
  PaymentMethodSaveRequest,
  PaymentMethodShareInitResponse,
  PaymentMethodShareRequestWrapper,
  PaymentMethodSummaryResponse,
  PaymentMethodTokenRequest,
} from '@monetize/types/app';
import { SetupIntentResult, Stripe, StripeElements } from '@stripe/stripe-js';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ROUTES } from '../constants';
import { logger } from '../services/logger';
import { useToast } from '../services/toast';
import { tempSharedAccountPaymentMethodState } from '../store/global.store';
import { showMicrodepositVerificationToast } from '../utils/stripe';
import { apiPost } from './axios';
import { savePaymentMethodOnStripe } from './shareService';

export const shareQueryKeys = {
  base: () => ['share'] as const,
  baseWithSecret: (secretId: string) =>
    [...shareQueryKeys.base(), secretId] as const,
  engagement: (recordId: string) =>
    [...shareQueryKeys.base(), 'engagement', recordId] as const,
  invoice: (secretId: string) =>
    [...shareQueryKeys.baseWithSecret(secretId), 'invoice'] as const,
  quote: (secretId: string) =>
    [...shareQueryKeys.baseWithSecret(secretId), 'quote'] as const,
  quotePdf: (secretId: string) =>
    [...shareQueryKeys.quote(secretId), 'pdf'] as const,
  paymentMethods: (secretId: string) =>
    [...shareQueryKeys.baseWithSecret(secretId), 'paymentMethods'] as const,
};

/**
 * PAYMENT METHOD SHARE
 * Get company information and payment method token from a payment method share link
 */
export function usePaymentShareInitData(
  options: Partial<
    UseMutationOptions<
      PaymentMethodShareInitResponse,
      unknown,
      PaymentMethodShareRequestWrapper<PaymentMethodTokenRequest>
    >
  > = {},
) {
  return useMutation<
    PaymentMethodShareInitResponse,
    unknown,
    PaymentMethodShareRequestWrapper<PaymentMethodTokenRequest>
  >({
    mutationFn: ({ accountId, tenantId, token, payload }) => {
      return apiPost<PaymentMethodShareInitResponse>(
        `/api/share/accounts/${accountId}/paymentMethods/data`,
        payload,
        {
          params: { token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data);
    },
    ...options,
  });
}

/**
 * PAYMENT METHOD SHARE
 * Save payment method to account and optionally bill group
 */
export function useCreatePaymentMethodFromShare(
  options: Partial<
    UseMutationOptions<
      PaymentMethodSummaryResponse,
      unknown,
      PaymentMethodShareRequestWrapper<{
        billGroupId?: string;
        paymentGatewayId: string;
        stripe: Stripe;
        elements: StripeElements;
        stripePublicKey: string;
      }>
    >
  > = {},
) {
  const { addToast } = useToast();
  const setTempPaymentMethodData = useSetAtom(
    tempSharedAccountPaymentMethodState,
  );

  const createPaymentMethod = async (
    accountId: string,
    tenantId: string,
    token: string,
    paymentGatewayId: string,
    setupIntentId: string,
    billGroupId?: string,
  ): Promise<PaymentMethodSummaryResponse> => {
    const paymentMethodSaveRequest: PaymentMethodSaveRequest = {
      paymentGatewayId,
      billGroupId,
      paymentMethod: {
        amount: 0,
        paymentToken: setupIntentId,
      },
    };

    const result = await apiPost<PaymentMethodSummaryResponse>(
      `/api/share/accounts/${accountId}/paymentMethods`,
      paymentMethodSaveRequest,
      {
        params: { token },
        axiosConfig: {
          excludeUserIdFromHeader: true,
          excludeTenantIdFromHeader: true,
          customXTenantId: tenantId,
        },
      },
    );

    return result.data;
  };

  // Helper function to check if redirect is needed and handle it
  const handleStripeResult = async (
    stripeResult: SetupIntentResult,
    accountId: string,
    tenantId: string,
    token: string,
    paymentGatewayId: string,
    billGroupId?: string,
  ): Promise<PaymentMethodSummaryResponse> => {
    const { error, setupIntent } = stripeResult;

    if (error) {
      logger.error('Error saving payment method to stripe', error);
      throw new Error(error.message);
    }

    if (setupIntent.status === 'requires_action' && setupIntent?.next_action) {
      if (
        setupIntent.next_action.type === 'redirect_to_url' &&
        setupIntent.next_action.redirect_to_url?.url
      ) {
        window.location.href = setupIntent.next_action.redirect_to_url.url;
        return {} as PaymentMethodSummaryResponse;
      } else if (setupIntent.next_action.type === 'verify_with_microdeposits') {
        const verificationUrl =
          setupIntent.next_action.verify_with_microdeposits
            ?.hosted_verification_url;

        if (verificationUrl) {
          showMicrodepositVerificationToast(addToast, verificationUrl);
          return {} as PaymentMethodSummaryResponse;
        }
        throw new Error(
          'Microdeposit verification required but no verification URL provided.',
        );
      }
    }

    setTempPaymentMethodData(null);
    const paymentMethodData = await createPaymentMethod(
      accountId,
      tenantId,
      token,
      paymentGatewayId,
      setupIntent.id,
      billGroupId,
    );

    return paymentMethodData;
  };

  return useMutation<
    PaymentMethodSummaryResponse,
    unknown,
    PaymentMethodShareRequestWrapper<{
      billGroupId?: string;
      paymentGatewayId: string;
      stripe: Stripe;
      elements: StripeElements;
      stripePublicKey: string;
    }>
  >({
    mutationFn: ({ accountId, tenantId, token, payload }) => {
      const {
        billGroupId,
        elements,
        paymentGatewayId,
        stripe,
        stripePublicKey,
      } = payload;

      // Store temp state before potential redirect
      setTempPaymentMethodData({
        accountId,
        tenantId,
        token,
        stripePublicKey,
        paymentGatewayId,
        billGroupId,
      });

      return savePaymentMethodOnStripe({
        stripe,
        elements,
        returnUrlPath: ROUTES.getPaymentMethodShareStripeCallbackRoute(
          tenantId,
          accountId,
          token,
          paymentGatewayId,
          billGroupId || '',
        ),
      }).then((result) =>
        handleStripeResult(
          result,
          accountId,
          tenantId,
          token,
          paymentGatewayId,
          billGroupId,
        ),
      );
    },
    ...options,
  });
}
